import type { TFunction } from 'i18next';
import type { IntakeSubmission } from '@/api/intake/intakeApi.types';
import type { Patient } from '@/api/patients/patientsApi.types';
import {
  resolveSpeciesLabel,
  type PatientOptionItem,
} from '@/api/patientOptions/patientOptionsApi';

export interface ProposedChangeRow {
  label: string;
  currentLabel: string;
  proposedLabel: string;
}

function describeCurrentValue(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return value;
}

export function buildProposedPetChanges(
  submission: IntakeSubmission,
  patient: Patient | null,
  t: TFunction,
  speciesOptions: PatientOptionItem[]
): ProposedChangeRow[] {
  const changes: ProposedChangeRow[] = [];

  if (submission.proposedPetName !== null) {
    changes.push({
      label: t('patients:form.name'),
      currentLabel: describeCurrentValue(patient === null ? null : patient.name),
      proposedLabel: submission.proposedPetName,
    });
  }
  if (submission.proposedSpecies !== null) {
    changes.push({
      label: t('patients:form.species'),
      currentLabel: describeCurrentValue(patient === null ? null : patient.species),
      proposedLabel: resolveSpeciesLabel(speciesOptions, submission.proposedSpecies),
    });
  }
  if (submission.proposedBreed !== null) {
    changes.push({
      label: t('patients:form.breed'),
      currentLabel: describeCurrentValue(patient === null ? null : patient.breed),
      proposedLabel: submission.proposedBreed,
    });
  }
  if (submission.proposedSex !== null) {
    changes.push({
      label: t('patients:form.sex'),
      currentLabel: patient === null || patient.sex === null ? '—' : t('common:sex.' + patient.sex),
      proposedLabel: t('common:sex.' + submission.proposedSex),
    });
  }
  if (submission.proposedDateOfBirth !== null) {
    changes.push({
      label: t('patients:form.dateOfBirth'),
      currentLabel: describeCurrentValue(patient === null ? null : patient.dateOfBirth),
      proposedLabel: submission.proposedDateOfBirth,
    });
  }
  if (submission.proposedWeight !== null) {
    changes.push({
      label: t('patients:form.weight'),
      currentLabel: describeCurrentValue(patient === null ? null : patient.weight),
      proposedLabel: submission.proposedWeight,
    });
  }
  if (submission.proposedNeutered !== null) {
    changes.push({
      label: t('patients:form.neutered'),
      currentLabel:
        patient === null || patient.neutered === null
          ? '—'
          : patient.neutered
            ? t('intake:petDetails.neuteredYes')
            : t('intake:petDetails.neuteredNo'),
      proposedLabel: submission.proposedNeutered
        ? t('intake:petDetails.neuteredYes')
        : t('intake:petDetails.neuteredNo'),
    });
  }
  return changes;
}

export interface OwnerReportedHealthRow {
  label: string;
  value: string;
}

export function buildOwnerReportedHealth(
  submission: IntakeSubmission,
  t: TFunction
): OwnerReportedHealthRow[] {
  const candidates = [
    { label: t('patients:form.medication'), value: submission.proposedMedication },
    { label: t('patients:form.pastSurgeries'), value: submission.proposedPastSurgeries },
    { label: t('patients:form.allergies'), value: submission.proposedAllergies },
  ];
  const rows: OwnerReportedHealthRow[] = [];
  for (const candidate of candidates) {
    if (candidate.value !== null && candidate.value.trim().length > 0) {
      rows.push({ label: candidate.label, value: candidate.value });
    }
  }
  return rows;
}
